import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SegmentedControl } from "@/components/layout-2/ds";
import { FieldFormula } from "@/types/formula";
import { constantKind, ConstantKind, constantOf, constantSource } from "@/utils/formula";
import { FormulaField } from "./formula-field";

type Mode = "fixed" | "formula";

type ConstantOrFormulaFieldProps = {
    id: string;
    formula: FieldFormula;
    /** The stored expression. Always an expression, whichever mode shows it. */
    value: string;
    /** Type of the value, when the form knows it (`formula.expects_from`). */
    type?: string | null;
    disabled?: boolean;
    large?: boolean;
    label?: string;
    description?: string;
    /** Shown in an empty fixed input: what leaving it empty means, when it means something. */
    placeholder?: string;
    onChange: (value: string) => void;
};

/**
 * A formula field that can be filled like an ordinary one: « Fixed value | Formula ».
 *
 * For the fields the server declares `constant_entry`. The stored string stays an
 * expression — a published graph holding `'id_1'` must keep reading it — so the fixed input
 * is only a way of *writing* one: it shows the value of a stored literal and writes
 * `quote()` of what is typed. Nobody has to know that text wants quotes.
 *
 * The mode is read off the value when the field opens — a pure literal of the right kind,
 * or nothing yet, opens fixed — and then kept, so a formula that happens to be a constant
 * while it is being typed does not swap inputs under the caret. Switching carries the value
 * across when it can: a fixed value becomes its literal, and a literal goes back to the
 * input; a computation switched to « Fixed value » is cleared, since it has no value to show.
 */
export function ConstantOrFormulaField({ id, formula, value, type, disabled, large, label, description, placeholder, onChange }: ConstantOrFormulaFieldProps) {
    const kind = constantKind(type ?? formula.expects);
    const [mode, setMode] = useState<Mode>(() => (constantOf(value, kind)[0] ? "fixed" : "formula"));
    const [isConstant, held] = constantOf(value, kind);

    const switchTo = (next: Mode) => {
        if (next === mode) return;
        if (next === "fixed" && !isConstant) onChange("");
        setMode(next);
    };

    return (
        <div className="flex flex-col gap-1.5" data-testid={`constant-or-formula-${id}`}>
            {!disabled && (
                <SegmentedControl<Mode>
                    ariaLabel="How the value is given"
                    value={mode}
                    onChange={switchTo}
                    options={[
                        { value: "fixed", label: "Fixed value" },
                        { value: "formula", label: "Formula" }
                    ]}
                />
            )}
            {mode === "fixed" ? (
                <ConstantInput id={id} kind={kind} value={isConstant ? held : ""} disabled={disabled} placeholder={placeholder} onChange={next => onChange(constantSource(next, kind))} />
            ) : (
                <FormulaField id={id} formula={{ ...formula, switchable: false }} value={value} disabled={disabled} large={large} label={label} description={description} onChange={onChange} />
            )}
        </div>
    );
}

type ConstantInputProps = {
    id: string;
    kind: ConstantKind;
    value: unknown;
    disabled?: boolean;
    placeholder?: string;
    onChange: (value: unknown) => void;
};

/** The plain input of one kind. Writes the raw value; the caller quotes it. */
function ConstantInput({ id, kind, value, disabled, placeholder, onChange }: ConstantInputProps) {
    const text = value === null || value === undefined ? "" : String(value);

    if (kind === "boolean") {
        return (
            <Select value={text === "" ? undefined : text} onValueChange={next => onChange(next === "true")} disabled={disabled}>
                <SelectTrigger id={id} className="rounded border px-2 py-1" data-testid={`constant-input-${id}`}>
                    <SelectValue placeholder="Yes or no" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="true">Yes</SelectItem>
                    <SelectItem value="false">No</SelectItem>
                </SelectContent>
            </Select>
        );
    }

    const inputType = kind === "number" ? "number" : kind === "date" ? "date" : kind === "datetime" ? "datetime-local" : "text";
    return (
        <Input
            id={id}
            name={id}
            type={inputType}
            value={kind === "datetime" ? text.slice(0, 16) : text}
            disabled={disabled}
            placeholder={placeholder ?? (kind === "text" ? "Type the value as it is, e.g. id_1" : kind === "number" ? "e.g. 1" : undefined)}
            data-testid={`constant-input-${id}`}
            onChange={event => onChange(event.target.value)}
        />
    );
}
